import React, { memo, useMemo, JSX } from 'react';
import PaperCard from 'src/components/PaperCard';
import StatTitle from 'src/components/StatTitle';
import useFetchApiData from 'src/hooks/useFetchApiData';
import { WidgetProps } from './types';
import { defaultWidgetSize } from './utils';
import DefaultLoadingSkeleton from 'src/components/LoadingSkeleton';
import FadeBox from '../FadeBox';

const Widget = <T extends unknown>({
  size = defaultWidgetSize,
  title,
  filters = {},
  Content,
  overrideData,
  LoadingSkeleton = <DefaultLoadingSkeleton />,
  overrideLoading,
  isWithContentFade = true,
  service,
}: WidgetProps<T>) => {
  const { data, loading } = useFetchApiData<T>({
    filters,
    overrideData,
    overrideLoading,
    service,
  });

  const { timeSpan } = filters;
  const { width, height } = size;

  const WrappedContent = useMemo(() => {
    if (loading && LoadingSkeleton && isWithContentFade) {
      return LoadingSkeleton;
    }
    const ContentElement = <Content data={data} loading={loading} />;
    if (isWithContentFade) {
      return <FadeBox loading={loading}>{ContentElement}</FadeBox>;
    }

    return ContentElement;
  }, [data, loading, Content, LoadingSkeleton, isWithContentFade]);

  return (
    <PaperCard sx={{ height, width }}>
      <StatTitle statName={title} timeSpan={timeSpan} />
      {WrappedContent}
    </PaperCard>
  );
};

export default memo(Widget) as <T extends unknown>(
  props: WidgetProps<T>,
) => JSX.Element;
